import React from "react";
import {
  UploadCloud, FileCode2, Layers, FileImage, FileSpreadsheet, Ticket, Boxes, Loader2,
  CheckCircle2, AlertTriangle, Download, ArrowRight, Trash2, ScanSearch,
} from "lucide-react";
import { card, th, td, btn, Pill } from "../../pages/cyberShared";
import { ACCEPT, MAX_UPLOAD_BYTES, exportUrl, isAiFormat } from "./bpmApi";

/**
 * Building blocks for the BPM Optimization → Import Process page.
 * Dark CARES style (see ChargebackPage): #0b2035 cards, #1c3d5c borders, #9d80ff accent.
 */

const ACCENT = "#9d80ff";

export const FORMATS = [
  { icon: FileCode2, label: "BPMN 2.0 (.bpmn, .xml)", status: "ready",
    note: "Exports from Camunda, Signavio, Bizagi, ARIS or Blueworks — imported exactly; a layout is generated if the file has none." },
  { icon: Layers, label: "Visio (.vsdx)", status: "ready",
    note: "Cross-functional (swimlane) flowcharts — shapes, lanes and connectors are converted to BPMN." },
  { icon: FileImage, label: "PDF, PNG, JPG", status: "ai",
    note: "Scanned or exported flowcharts — Claude on Amazon Bedrock reads the picture and the BPMN is built from what it finds." },
  { icon: FileSpreadsheet, label: "XES / CSV event logs", status: "soon",
    note: "Process mining: discover the as-is process from case/activity/timestamp logs." },
  { icon: Ticket, label: "ServiceNow task history", status: "soon",
    note: "Mine real hand-offs and wait times from ticket and task audit trails." },
  { icon: Boxes, label: "ArchiMate", status: "soon",
    note: "Business-process and application-layer views from EA repositories." },
];

const STATUS = {
  ready: { bg: "#0f323c", fg: "#85e3af", text: "Supported" },
  ai: { bg: "#1a2a49", fg: "#c4b3ff", text: "AI extraction" },
  soon: { bg: "#102942", fg: "#8aa0b6", text: "Coming soon" },
};

export function FormatGuide() {
  return (
    <div style={{ ...card, padding: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#e8eef5", marginBottom: 8 }}>
        Supported formats
      </div>
      {FORMATS.map((f) => {
        const s = STATUS[f.status];
        const Icon = f.icon;
        return (
          <div key={f.label} style={{ display: "flex", gap: 10, padding: "8px 0",
                                      borderBottom: "1px solid #102942",
                                      opacity: f.status === "soon" ? 0.65 : 1 }}>
            <Icon size={16} style={{ color: f.status === "soon" ? "#6b8aa8" : ACCENT, flexShrink: 0,
                                     marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "#e8eef5" }}>{f.label}</span>
                <Pill tone={s}>{s.text}</Pill>
              </div>
              <div style={{ fontSize: 11.5, color: "#8aa0b6", marginTop: 2, lineHeight: 1.45 }}>{f.note}</div>
            </div>
          </div>
        );
      })}
      <div style={{ fontSize: 11, color: "#6b8aa8", marginTop: 8 }}>
        Up to 20 MB. PowerPoint: export the slide as PDF or PNG first. PDFs longer than five pages
        are trimmed to the first five for AI extraction.
      </div>
    </div>
  );
}

export function Dropzone({ onFile, disabled }) {
  const [over, setOver] = React.useState(false);
  const [err, setErr] = React.useState("");
  const inputRef = React.useRef(null);

  const take = (file) => {
    setErr("");
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setErr(`${file.name} is ${(file.size / 1e6).toFixed(1)} MB — the limit is 20 MB.`);
      return;
    }
    onFile(file);
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => { if (!disabled && (e.key === "Enter" || e.key === " ")) inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (!disabled) take(e.dataTransfer.files?.[0]);
        }}
        style={{
          ...card, padding: "34px 20px", textAlign: "center", cursor: disabled ? "default" : "pointer",
          border: `2px dashed ${over ? ACCENT : "#2c567b"}`,
          background: over ? "#14274a" : "#0b2035", transition: "background .15s, border-color .15s",
          opacity: disabled ? 0.6 : 1,
        }}>
        <UploadCloud size={34} style={{ color: ACCENT, display: "block", margin: "0 auto" }} />
        <div style={{ fontSize: 14.5, fontWeight: 700, color: "#e8eef5", marginTop: 8 }}>
          Drop a process file here, or click to choose
        </div>
        <div style={{ fontSize: 12, color: "#8aa0b6", marginTop: 4 }}>
          .bpmn · .xml · .vsdx · .pdf · .png · .jpg
        </div>
        <input ref={inputRef} type="file" accept={ACCEPT} style={{ display: "none" }}
               onChange={(e) => { take(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
      {err && <div style={{ fontSize: 12, color: "#ffa685", marginTop: 6 }}>{err}</div>}
    </div>
  );
}

const AI_STEPS = [
  "Uploading the file",
  "Claude is reading the diagram — lanes, steps, decisions",
  "Building BPMN 2.0 and laying out the diagram",
];

/** Progress for an import in flight. AI extraction typically takes 20–60 s. */
export function ImportProgress({ file, startedAt }) {
  const ai = isAiFormat(file?.name);
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, Math.round((now - startedAt) / 1000));
  const stage = !ai ? 0 : secs < 2 ? 0 : secs < 45 ? 1 : 2;
  const pct = ai ? Math.min(95, 5 + (secs / 60) * 90) : 60;
  return (
    <div style={{ ...card, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700,
                    color: "#e8eef5" }}>
        <Loader2 size={15} className="animate-spin" style={{ color: ACCENT }} />
        {ai ? "Extracting the process with AI" : "Importing"} — {file?.name}
        <span style={{ marginLeft: "auto", fontSize: 11.5, color: "#8aa0b6", fontWeight: 500 }}>{secs}s</span>
      </div>
      <div style={{ height: 6, background: "#102942", borderRadius: 99, marginTop: 12, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: ACCENT, borderRadius: 99,
                      transition: "width .5s linear" }} />
      </div>
      {ai && (
        <>
          <ol style={{ margin: "12px 0 0 18px", padding: 0, fontSize: 12, color: "#8aa0b6", lineHeight: 1.8 }}>
            {AI_STEPS.map((s, i) => (
              <li key={s} style={{ color: i === stage ? "#e8eef5" : i < stage ? "#85e3af" : "#6b8aa8",
                                   fontWeight: i === stage ? 600 : 400 }}>{s}</li>
            ))}
          </ol>
          <div style={{ fontSize: 11.5, color: "#6b8aa8", marginTop: 6 }}>
            This usually takes 20–60 seconds.
          </div>
        </>
      )}
    </div>
  );
}

const confTone = (c) => (c >= 0.85 ? "#34d17a" : c >= 0.6 ? "#facb7e" : "#ff6a33");
const FORMAT_LABEL = { bpmn: "BPMN 2.0", vsdx: "Visio", pdf: "PDF (AI)", png: "PNG (AI)", jpeg: "JPG (AI)" };
export const formatLabel = (f) => FORMAT_LABEL[f] || String(f || "").toUpperCase();

export function ConfidenceBadge({ value }) {
  if (value === null || value === undefined) return <span style={{ color: "#6b8aa8" }}>—</span>;
  const c = confTone(value);
  return (
    <span title="How faithfully the imported model is expected to match the source"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: c }}>
      <span style={{ width: 46, height: 6, borderRadius: 99, background: "#102942", overflow: "hidden",
                     display: "inline-block" }}>
        <span style={{ display: "block", height: "100%", width: `${Math.round(value * 100)}%`, background: c }} />
      </span>
      {Math.round(value * 100)}%
    </span>
  );
}

export function ImportResultCard({ result, preview, onAnalyze, onDismiss }) {
  const lowConf = result.confidence < 0.6;
  return (
    <div style={{ ...card, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
        {lowConf ? <AlertTriangle size={18} style={{ color: "#facb7e", marginTop: 2 }} />
                 : <CheckCircle2 size={18} style={{ color: "#34d17a", marginTop: 2 }} />}
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#e8eef5" }}>{result.name}</div>
          <div style={{ fontSize: 12, color: "#8aa0b6", marginTop: 3, display: "flex", gap: 14,
                        flexWrap: "wrap", alignItems: "center" }}>
            <span>{formatLabel(result.source_format)}</span>
            <span>{result.step_count} activities · {result.node_count ?? "—"} elements</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Confidence <ConfidenceBadge value={result.confidence} />
            </span>
            {result.model && <span style={{ color: "#6b8aa8" }}>{result.model}</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <a href={exportUrl(result.id)} download style={{ ...btn, textDecoration: "none" }}>
            <Download size={13} /> Download BPMN
          </a>
          <button style={{ ...btn, background: ACCENT, borderColor: ACCENT, color: "#0b2035" }}
                  onClick={() => onAnalyze(result.id)}>
            <ScanSearch size={13} /> Analyze this process <ArrowRight size={13} />
          </button>
          {onDismiss && (
            <button style={btn} onClick={onDismiss} title="Import another file">Import another</button>
          )}
        </div>
      </div>

      {result.notes?.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6b8aa8", textTransform: "uppercase",
                        letterSpacing: .4, marginBottom: 4 }}>Extraction notes</div>
          <ul style={{ margin: "0 0 0 18px", padding: 0, fontSize: 12, color: "#c3d2e0", lineHeight: 1.6 }}>
            {result.notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      )}
      <div style={{ marginTop: 12 }}>{preview}</div>
    </div>
  );
}

export function ImportsTable({ items, loading, error, onAnalyze, onDelete, deleting }) {
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "12px 14px", fontSize: 13, fontWeight: 700, color: "#e8eef5",
                    borderBottom: "1px solid #1c3d5c" }}>
        Imported processes
      </div>
      {error && <div style={{ padding: 14, fontSize: 12, color: "#ffa685" }}>{error}</div>}
      {!error && !loading && items.length === 0 && (
        <div style={{ padding: 14, fontSize: 12, color: "#6b8aa8" }}>
          Nothing imported yet. Imported processes appear in Process Analysis under “Imported”.
        </div>
      )}
      {items.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Name</th><th style={th}>Source</th><th style={th}>File</th>
                <th style={th}>Activities</th><th style={th}>Confidence</th><th style={th}>Imported</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...td, color: "#e8eef5", fontWeight: 600 }}>{r.name}</td>
                  <td style={td}>{formatLabel(r.source_format)}</td>
                  <td style={{ ...td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis",
                               whiteSpace: "nowrap" }} title={r.original_filename}>{r.original_filename}</td>
                  <td style={td}>{r.step_count}</td>
                  <td style={td}><ConfidenceBadge value={r.confidence} /></td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    {r.created_at ? String(r.created_at).slice(0, 16).replace("T", " ") : "—"}
                    {r.created_by && r.created_by !== "anonymous" &&
                      <div style={{ fontSize: 10.5, color: "#6b8aa8" }}>{r.created_by}</div>}
                  </td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                    <button style={{ ...btn, padding: "4px 9px", fontSize: 11 }} onClick={() => onAnalyze(r.id)}>
                      <ScanSearch size={12} /> Analyze
                    </button>{" "}
                    <a href={exportUrl(r.id)} download title="Download BPMN"
                       style={{ ...btn, padding: "4px 9px", fontSize: 11, textDecoration: "none" }}>
                      <Download size={12} />
                    </a>{" "}
                    <button style={{ ...btn, padding: "4px 9px", fontSize: 11, color: "#ffa685" }}
                            disabled={deleting === r.id}
                            onClick={() => onDelete(r)} title="Delete">
                      {deleting === r.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
